/**
 * Shared filter vocabulary for the Trailers (/trailers) and Yard View
 * (/yard-view) pages. Both pages send these values straight to the
 * `list_trailers_page` / `list_yard_trailers` database functions, so the
 * option values here must match the strings those functions compare against.
 */

export type TrailerFilterKey =
  | "type"
  | "scac"
  | "status"
  | "dwell"
  | "event"
  | "docs"
  | "location"
  | "service";

export interface TrailerFilterOption {
  label: string;
  value: string;
}

export const TRAILER_FILTER_LABELS: Record<TrailerFilterKey, string> = {
  type: "Type",
  scac: "SCAC",
  status: "Status",
  dwell: "Dwell Time",
  event: "Most Recent Event",
  docs: "Documents",
  location: "Location",
  service: "Service",
};

export const LOCATION_STATUS_LABELS: Record<string, string> = {
  at_site: "At Site",
  in_transit: "In Transit",
  unknown: "Unknown",
};

export const EVENT_TYPE_LABELS: Record<string, string> = {
  checked_in: "Checked In",
  checked_out: "Checked Out",
  audited: "Audited",
  driver_verified: "Driver Verified",
};

/** Full status list (Trailers page). Yard View hides `unknown` by design. */
export const STATUS_FILTER_OPTIONS: TrailerFilterOption[] = [
  { label: "In Transit", value: "in_transit" },
  { label: "At Site", value: "at_site" },
  { label: "Unknown", value: "unknown" },
];

export const YARD_STATUS_FILTER_OPTIONS: TrailerFilterOption[] =
  STATUS_FILTER_OPTIONS.filter((o) => o.value !== "unknown");

export const DWELL_FILTER_OPTIONS: TrailerFilterOption[] = [
  { label: "Less than 24 hr", value: "short" },
  { label: "24 hr or more", value: "long" },
];

export const EVENT_FILTER_OPTIONS: TrailerFilterOption[] = [
  { label: "Checked In", value: "checked_in" },
  { label: "Checked Out", value: "checked_out" },
  { label: "Found during audit", value: "audit_found" },
  { label: "Not found during audit", value: "audit_not_found" },
  { label: "Audited", value: "audited" },
];

export const DOCS_FILTER_OPTIONS: TrailerFilterOption[] = [
  { label: "Has Bill of Lading", value: "has_bol" },
  { label: "Missing Bill of Lading", value: "missing_bol" },
];

export const SERVICE_FILTER_OPTIONS: TrailerFilterOption[] = [
  { label: "Needs Service", value: "needs_service" },
  { label: "No Service Needed", value: "ok" },
];

/**
 * Chip filters use `["all"]` as their "no filter" value. The database
 * functions expect NULL for that case.
 */
export function toArrayOrNull(v: string[] | undefined | null): string[] | null {
  if (!v || v.length === 0 || v.includes("all")) return null;
  return v;
}

export function isFilterActive(v: string[] | undefined | null): boolean {
  return toArrayOrNull(v) !== null;
}

/** Shape returned by both list functions for a single trailer row. */
export interface TrailerListRow {
  id: string;
  trailer_id: string;
  trailer_type_id: string | null;
  scac: string | null;
  load_status: string | null;
  seal_id: string | null;
  image_path: string | null;
  current_location_id: string | null;
  current_location_status: string;
  is_customer_parking?: boolean | null;
  needs_service?: boolean | null;
  needs_service_reason?: string | null;
  doc_count: number;
  /** Timestamp of the most recent check-in; dwell is measured from here. */
  dwell_since?: string | null;
  current_location: { short_code: string; city?: string | null; track_in_yms?: boolean | null } | null;
  latest_event: {
    id: string;
    event_type: string;
    occurred_at: string;
    notes: string | null;
    location: { short_code: string } | null;
  } | null;
  associated_orders: {
    id: string;
    unique_id: string;
    trailer_id: string | null;
    status: string;
    ship_from_city: string | null;
    ship_to_city: string | null;
  }[];
}
